import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/core/supabase';
import { todayIn } from '@/core/dates';

export interface NotificationRow {
  id: string;
  category: string;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  body: string | null;
  entity: string | null;
  entity_id: string | null;
  action_url: string | null;
  occurrence: number;
  read_at: string | null;
  resolved_at: string | null;
  created_at: string;
}

/**
 * Open alerts, newest first. RLS already limits these to notifications
 * addressed to this user or to a permission they hold, so there is no filter
 * here — the database has done it.
 */
export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    refetchInterval: 60_000,
    queryFn: async (): Promise<NotificationRow[]> => {
      const { data, error } = await supabase
        .from('notifications')
        .select(`
          id, category, severity, title, body, entity, entity_id,
          action_url, occurrence, read_at, resolved_at, created_at
        `)
        .is('resolved_at', null)
        .order('severity', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      return (data ?? []) as NotificationRow[];
    },
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      if (ids.length === 0) return 0;
      const { data, error } = await supabase.rpc('mark_notifications_read', {
        p_ids: ids,
      });
      if (error) throw error;
      return data as number;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * The absence sweep, alert generation and document expiry check. Normally on a
 * schedule; exposed here so an admin who has just fixed something does not have
 * to wait until tomorrow to see it reflected.
 */
export function useRunDailyChecks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (date?: string) => {
      const { data, error } = await supabase.rpc('run_daily_checks', {
        p_date: date ?? todayIn(),
      });
      if (error) throw error;
      return data as {
        date: string;
        queuedForRecompute: number;
        absenceAlerts: number;
        overdueLeaveAlerts: number;
        documentAlerts: number;
      };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      void queryClient.invalidateQueries({ queryKey: ['attendance-dirty-count'] });
    },
  });
}
